import {
	type ContentPart,
	isReasoning,
	isSource,
	isToolCall,
	type ToolCallPart,
} from "./contentPart";

export type OrderedActivityStep =
	| { kind: "reasoning"; text: string }
	| { kind: "activeTool"; name: string }
	| { kind: "webSearch"; sourceCount: number }
	| { kind: "tool"; call: ToolCallPart };

const CARD_TOOLS = new Set([
	"respondWithPlan",
	"askQuestion",
	"createClioCalendarEntry",
]);

export function orderActivitySteps(
	parts: ContentPart[],
	activeToolName?: string | null,
	inFlight = false,
	showWebSearchProbe = false,
): { steps: OrderedActivityStep[]; allToolCallCount: number } {
	const steps: OrderedActivityStep[] = [];
	const webSourceUrls = new Set<string>();
	let allToolCallCount = 0;
	let hasReasoning = false;
	for (const part of parts) {
		if (
			isSource(part) &&
			typeof part.url === "string" &&
			(part.url.startsWith("https://") || part.url.startsWith("http://"))
		) {
			webSourceUrls.add(part.url);
		}
		if (isReasoning(part)) {
			if (part.text.length > 0) {
				hasReasoning = true;
				steps.push({ kind: "reasoning", text: part.text });
			}
			continue;
		}
		if (!isToolCall(part)) continue;
		allToolCallCount += 1;
		if (!CARD_TOOLS.has(part.toolName)) {
			steps.push({ kind: "tool", call: part });
		}
	}
	if (webSourceUrls.size > 0) {
		steps.push({ kind: "webSearch", sourceCount: webSourceUrls.size });
	} else if (
		showWebSearchProbe &&
		hasReasoning &&
		allToolCallCount === 0 &&
		!activeToolName
	) {
		steps.push({ kind: "webSearch", sourceCount: 0 });
	}
	if (
		inFlight &&
		activeToolName &&
		!steps.some(
			(step) => step.kind === "tool" && step.call.toolName === activeToolName,
		)
	) {
		steps.push({ kind: "activeTool", name: activeToolName });
	}
	return { steps, allToolCallCount };
}

export function findToolActivity(
	parts: ContentPart[],
	toolCallId: string,
): { stepNumber: number; stepCount: number; toolName: string } | null {
	const { steps } = orderActivitySteps(parts);
	const index = steps.findIndex(
		(step) => step.kind === "tool" && step.call.toolCallId === toolCallId,
	);
	if (index < 0) return null;
	const step = steps[index];
	if (step.kind !== "tool") return null;
	return {
		stepNumber: index + 1,
		stepCount: steps.length,
		toolName: step.call.toolName,
	};
}
